import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
const ShapeFilterContainer = styled.div `
  margin-bottom: 20px;
`;
const ShapeFilterTitle = styled.h4 `
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  margin-top: 0;
`;
const ShapeFilterGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
`;
const ShapeImageWrapper = styled.div `
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const ComingSoonShape = styled.div `
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: 1px dashed var(--border-color);
  border-radius: 4px;
  color: var(--text-tertiary);
  font-size: 7px;
  font-weight: 500;
  text-align: center;
  line-height: 1.1;
  transition: all 0.2s ease;
`;
const ShapeFilterButton = styled.button `
  background: ${props => props.$disabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)'};
  border: ${props => props.$disabled ? '1px solid var(--border-color)' :
    props.$isActive ? '2px solid var(--accent-gold, #996515)' : '1px solid var(--border-color)'};
  padding: 10px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 80px;
  opacity: ${props => props.$disabled ? 0.4 : 1};

  &:hover {
    border-color: ${props => props.$disabled ? 'var(--border-color)' : 'var(--accent-gold-border, rgba(153, 101, 21, 0.25))'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 8px rgba(0,0,0,0.1)'};
  }

  &:hover ${ComingSoonShape} {
    border-color: var(--accent-gold-border, rgba(153, 101, 21, 0.25));
  }

  .shape-image {
    width: 32px;
    height: 32px;
    object-fit: contain;
    opacity: ${props => props.$isActive ? 1 : 0.6};
    transition: all 0.2s ease;
  }

  &:hover .shape-image {
    opacity: 1;
    transform: scale(1.1);
  }

  .shape-label {
    font-size: 11px;
    font-weight: 500;
    color: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--text-primary)'};
    text-align: center;
    line-height: 1.2;
  }

  .shape-count {
    font-size: 10px;
    color: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--text-tertiary)'};
  }
`;
const AllShapesButton = styled(ShapeFilterButton) `
  background: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--bg-secondary)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};

  .shape-label {
    font-weight: 600;
    color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  }

  .shape-count {
    color: ${props => props.$isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-tertiary)'};
  }
`;
// Individual shape image component with error handling
const ShapeImage = ({ shape, imageShapeName, imageUrlBase, hasImage, isActive }) => {
    const [imageError, setImageError] = useState(false);
    if (!hasImage || imageError) {
        return (_jsx(ShapeImageWrapper, { children: _jsxs(ComingSoonShape, { children: ["IMG", _jsx("br", {}), "SOON"] }) }));
    }
    return (_jsx(ShapeImageWrapper, { children: _jsx("img", { src: `${imageUrlBase}/${imageShapeName}.png`, alt: shape, className: "shape-image", onError: () => setImageError(true) }) }));
};
export const ShapeFilter = ({ selectedShape, shapes, onShapeChange, getShapeCount, availableShapeImages = ['round', 'oval', 'princess', 'emerald', 'asscher', 'cushion', 'marquise', 'radiant', 'pear', 'heart'], imageUrlBase = 'https://blobaneesh.blob.core.windows.net/images/diamondshape' }) => {
    // Ensure selected shape is in the list even if it has 0 results (for AI search)
    const displayShapes = selectedShape && !shapes.some(s => s.toLowerCase() === selectedShape.toLowerCase())
        ? [...shapes, selectedShape].sort()
        : shapes;
    const totalCount = displayShapes.reduce((sum, shape) => sum + getShapeCount(shape), 0);
    // Helper to get image shape name (handles variations like "Cushion Modified")
    const getImageShapeName = (shape) => {
        const shapeLower = shape.toLowerCase();
        // Map shape variations to their base image names
        if (shapeLower.includes('cushion'))
            return 'cushion';
        if (shapeLower.includes('round') || shapeLower.includes('brilliant'))
            return 'round';
        if (shapeLower.includes('oval'))
            return 'oval';
        if (shapeLower.includes('princess'))
            return 'princess';
        if (shapeLower.includes('emerald'))
            return 'emerald';
        if (shapeLower.includes('asscher'))
            return 'asscher';
        if (shapeLower.includes('marquise'))
            return 'marquise';
        if (shapeLower.includes('radiant'))
            return 'radiant';
        if (shapeLower.includes('pear'))
            return 'pear';
        if (shapeLower.includes('heart'))
            return 'heart';
        return shapeLower;
    };
    return (_jsxs(ShapeFilterContainer, { children: [_jsx(ShapeFilterTitle, { children: "Diamond Shapes" }), _jsxs(ShapeFilterGrid, { children: [_jsxs(AllShapesButton, { "$isActive": !selectedShape, onClick: () => onShapeChange(''), children: [_jsx("span", { className: "shape-label", children: "All Shapes" }), _jsx("span", { className: "shape-count", children: totalCount })] }), displayShapes.map(shape => {
                        const shapeLower = shape.toLowerCase();
                        const imageShapeName = getImageShapeName(shape);
                        const hasImage = availableShapeImages.includes(imageShapeName);
                        const count = getShapeCount(shape);
                        const isDisabled = count === 0;
                        const isFromAISearch = !shapes.some(s => s.toLowerCase() === shapeLower) && selectedShape.toLowerCase() === shapeLower;
                        const isActive = selectedShape.toLowerCase() === shapeLower;
                        return (_jsxs(ShapeFilterButton, { "$isActive": isActive, "$disabled": isDisabled, onClick: () => onShapeChange(isDisabled ? '' : shape), title: isFromAISearch
                                ? `${shape} (0 diamonds - from AI search, click to clear)`
                                : `${shape} (${count} diamonds)`, children: [_jsx(ShapeImage, { shape: shape, imageShapeName: imageShapeName, imageUrlBase: imageUrlBase, hasImage: hasImage, isActive: isActive }), _jsx("span", { className: "shape-label", children: shape }), _jsx("span", { className: "shape-count", children: count })] }, shape));
                    })] })] }));
};
